import React from 'react';
import { Box, Typography, Card } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';
import Button from '../Button';
import servicePic1 from '../../Media/image-1.png';
import servicePic2 from '../../Media/image-2.png';
import servicePic3 from '../../Media/image-3.png';
import servicePic4 from '../../Media/image-4.png';
import servicePic5 from '../../Media/image-5.png';
import servicePic6 from '../../Media/image-6.png';
import servicePic7 from '../../Media/image-7.png';
import servicePic8 from '../../Media/image-8.png';
import servicePic9 from '../../Media/image-9.png';

const services = [
  { 
    title: 'HOME\nPURCHASE\nMORTGAGE', 
    image: servicePic1,
    ariaLabel: 'Learn about our home purchase mortgage options'
  },
  { 
    title: 'SELF EMPLOYED\nMORTGAGE', 
    image: servicePic2,
    ariaLabel: 'Explore mortgage solutions for self-employed individuals'
  },
  { 
    title: 'NEW CONSTRUCTION\nMORTGAGE', 
    image: servicePic3,
    ariaLabel: 'Discover our new construction mortgage options'
  },
  { 
    title: 'NEW TO\nCANADA', 
    image: servicePic4,
    ariaLabel: 'Find mortgage solutions for newcomers to Canada'
  },
  { 
    title: 'SPOUSAL BUYOUT\nAND DIVORCE', 
    image: servicePic5,
    ariaLabel: 'Learn about mortgage options for spousal buyout and divorce'
  },
  { 
    title: 'HOME IMPROVEMENT\nMORTGAGE', 
    image: servicePic6,
    ariaLabel: 'Explore home improvement and renovation mortgage options'
  },
  { 
    title: 'INVESTMENT\nPROPERTY', 
    image: servicePic7,
    ariaLabel: 'Discover mortgage solutions for investment properties'
  },
  { 
    title: 'REFINANCING &\nDEBT CONSOLIDATION', 
    image: servicePic8,
    ariaLabel: 'Learn about refinancing and debt consolidation options'
  },
  { 
    title: 'CHIP REVERSE\nMORTGAGE', 
    image: servicePic9,
    ariaLabel: 'Explore CHIP reverse mortgage solutions'
  }
];

const viewportConfig = { triggerOnce: false, rootMargin: "-100px 0px -100px 0px" };

const sectionVariants = {
  hidden: { opacity: 0, y: 50 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: "easeOut",
      staggerChildren: 0.09
    }
  }
};

const cardVariants = {
  hidden: { opacity: 0, scale: 0.8, rotateY: 15 },
  visible: {
    opacity: 1,
    scale: 1,
    rotateY: 0,
    transition: {
      duration: 0.6,
      type: "spring",
      stiffness: 100,
      damping: 15
    }
  }
};

const titleVariants = {
  hidden: { opacity: 0, x: -50 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const ServiceCard = ({ service }) => {
  const [ref, inView] = useInView(viewportConfig);
  return (
    <AnimatePresence>
      {inView && (
        <Card
          ref={ref}
          component={motion.div}
          variants={cardVariants}
          initial="hidden"
          animate="visible"
          exit="hidden"
          role="link"
          tabIndex={0}
          aria-label={service.ariaLabel}
          sx={{
            position: 'relative',
            width: { xs: '280px', md: '300px' },
            height: { xs: '280px', md: '300px' },
            borderRadius: '15px',
            overflow: 'hidden',
            cursor: 'pointer',
            flexShrink: 0,
            '&:hover': {
              '& .overlay': { bgcolor: 'rgba(0, 94, 131, 0.7)' },
              '& .title': { transform: 'scale(1.05)' }
            }
          }}
        >
          <Box
            component="img"
            src={service.image}
            alt={service.title}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover'
            }}
          />
          <Box
            className="overlay"
            component={motion.div}
            initial={{ backgroundColor: 'rgba(0, 94, 131, 0.5)' }}
            whileHover={{ backgroundColor: 'rgba(0, 94, 131, 0.7)' }}
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              transition: 'all 0.3s ease-in-out',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Typography
              className="title"
              component={motion.div}
              initial={{ opacity: 1, y: 0 }}  // Titles visible from the start
              whileHover={{ scale: 1.05 }}
              variant="h6"
              sx={{
                color: 'white',
                textAlign: 'center',
                whiteSpace: 'pre-line',
                fontWeight: 'bold',
                transition: 'all 0.3s ease-in-out'
              }}
            >
              {service.title}
            </Typography>
          </Box>
        </Card>
      )}
    </AnimatePresence>
  );
};

const ServicesSection = () => {
  // Use a hook for the overall section too.
  const [sectionRef, inViewSection] = useInView(viewportConfig);

  return (
    <Box
      ref={sectionRef}
      component={motion.div}
      variants={sectionVariants}
      initial="hidden"
      animate={inViewSection ? "visible" : "hidden"}
      sx={{ 
        width: '100%',
        py: { xs: 6, md: 8 },
        px: { xs: 2, md: 4 }
      }}
    >
      <Box sx={{ maxWidth: '1200px', mx: 'auto', width: '100%' }}>
        {/* Enhanced Title Animation */}
        <Typography 
          component={motion.div}
          variants={titleVariants}
          initial="hidden"
          whileInView="visible"
          viewport={viewportConfig}
          variant="h4" 
          sx={{ 
            textAlign: 'left', 
            color: '#64B5F6',
            fontWeight: 'bold',
            mb: 6,
            fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
            position: 'relative',
            '&::after': {
              content: '""',
              position: 'absolute',
              left: 0,
              bottom: -10,
              width: '80px',
              height: '4px',
              background: 'linear-gradient(to right, #64B5F6, transparent)',
              borderRadius: '2px'
            }
          }}
        >
          Our Services
        </Typography>
        
        {/* Slider Container */}
        <Box sx={{ 
          width: '100%',
          position: 'relative',
          display: 'flex',
          justifyContent: 'center',
          overflow: 'hidden'
        }}>
          <Box sx={{
            width: '100%',
            overflow: 'hidden',
            position: 'relative',
            mx: 'auto',
            '&::before, &::after': {
              content: '""',
              position: 'absolute',
              top: 0,
              height: '100%',
              width: '5vw',
              zIndex: 2,
              pointerEvents: 'none'
            },
            '&::before': {
              left: 0,
              background: 'linear-gradient(to right, white 0%, rgba(255,255,255,0.5) 30%, transparent 100%)'
            },
            '&::after': {
              right: 0,
              background: 'linear-gradient(to left, white 0%, rgba(255,255,255,0.5) 30%, transparent 100%)'
            }
          }}>
            {/* Slider with Animation */}
            <Box 
              component={motion.div}
              sx={{
                display: 'flex',
                gap: 3,
                width: 'fit-content',
                animation: 'slide 40s linear infinite',
                '@keyframes slide': {
                  '0%': { transform: 'translateX(0)' },
                  '100%': { transform: 'translateX(calc(-100%))' }
                },
                '&:hover': { animationPlayState: 'paused' }
              }}
            >
              {[...services, ...services, ...services].map((service, index) => (
                <Card
                  key={index}
                  component={motion.div}
                  variants={cardVariants}
                  sx={{
                    position: 'relative',
                    width: { xs: '280px', md: '300px' },
                    height: { xs: '280px', md: '300px' },
                    borderRadius: '15px',
                    overflow: 'hidden',
                    cursor: 'pointer',
                    flexShrink: 0,
                    '&:hover': {
                      '& .overlay': {
                        bgcolor: 'rgba(0, 94, 131, 0.7)',
                      },
                      '& .title': {
                        transform: 'translateY(0)',
                        opacity: 1
                      }
                    }
                  }}
                >
                  <Box
                    component="img"
                    src={service.image}
                    alt={service.title}
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                  />
                  <Box
                    className="overlay"
                    component={motion.div}
                    initial={{ backgroundColor: 'rgba(0, 94, 131, 0.5)' }}
                    whileHover={{ backgroundColor: 'rgba(0, 94, 131, 0.7)' }}
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      transition: 'all 0.3s ease-in-out',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Typography
                      className="title"
                      component={motion.div}
                      initial={{ opacity: 1, y: 0 }}
                      whileHover={{ opacity: 1, y: 20 }}
                      variant="h6"
                      sx={{
                        color: 'white',
                        textAlign: 'center',
                        whiteSpace: 'pre-line',
                        fontWeight: 'bold',
                        transition: 'all 0.3s ease-in-out'
                      }}
                    >
                      {service.title}
                    </Typography>
                  </Box>
                </Card>
              ))}
            </Box>
          </Box>
        </Box>

        {/* Buttons container using your provided code */}
        <Box 
          component={motion.div}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={viewportConfig}
          transition={{ duration: 0.6, delay: 0.3 }}
          sx={{ 
            display: 'flex', 
            justifyContent: 'center',
            gap: 2,
            mt: 6,
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: 'center'
          }}
        >
          
          
          <Button 
            component={motion.a}
            initial={{ scale: 0.9, x: 20 }}
            whileInView={{ scale: 1, x: 0 }}
            whileTap={{ scale: 0.95 }}
            viewport={viewportConfig}
            transition={{ delay: 0.5, type: "spring" }}
            variant="primary"
            sx={{ width: { xs: '100%', sm: 'auto' } }}
            href="https://velocity.newton.ca/sso/public.php?sc=4zh515s047gn"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Start your mortgage application process"
          >
            Complete Application
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default ServicesSection;
